/**
 * Phase 22 iter_202 — Public Membership page with plan recognition,
 * duplicate protection, and Stripe Billing Portal for Manage.
 *
 * Rules:
 *  • Fetches /memberships/me and renders "Your Current RGM Membership"
 *    strip when an active plan exists.
 *  • Active plan card → "✓ Current Plan" (no purchase button).
 *  • Monthly member → yearly card CTA becomes "Upgrade to Yearly".
 *  • Yearly member → monthly card CTA hidden.
 *  • Founding Artist / complimentary → all paid cards show
 *    "🏆 Lifetime Access — every future tier unlocked" (no CTA).
 *  • "Manage Membership" opens Stripe Billing Portal via
 *    /memberships/billing-portal.
 *  • Cache invalidation: reloads on `rgm.membership.updated` event.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  active: "Active",
  grace: "Grace Period",
  past_due: "Past Due",
  canceled_at_period_end: "Canceled at Period End",
  canceled: "Canceled",
};

const STATUS_TONE = {
  active: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  grace: "bg-amber-500/15 border-amber-500/40 text-amber-200",
  past_due: "bg-rose-500/15 border-rose-500/40 text-rose-200",
  canceled_at_period_end: "bg-slate-500/15 border-slate-500/40 text-slate-200",
  canceled: "bg-slate-500/15 border-slate-500/40 text-slate-200",
};

const statusKey = (active) => {
  if (!active) return "active";
  if (active.status && active.status !== "active") return active.status;
  if (active.cancel_at_period_end) return "canceled_at_period_end";
  return "active";
};

const formatRenewal = (iso, isLifetime) => {
  if (isLifetime) return "No renewal required — lifetime";
  if (!iso) return null;
  try {
    return `Renews ${new Date(iso).toLocaleDateString()}`;
  } catch {
    return null;
  }
};

const MembershipPage = () => {
  const [plans, setPlans] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [me, setMe] = useState(null); // active_summary
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [busyPortal, setBusyPortal] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [plansRes, meRes] = await Promise.all([
        api.get("/memberships/plans"),
        api.get("/memberships/me").catch(() => ({ data: {} })),
      ]);
      setPlans(plansRes.data?.plans || []);
      setEnabled(!!plansRes.data?.enabled);
      setMe(meRes.data?.active_summary || null);
    } catch {
      toast.error("Could not load memberships");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("rgm.membership.updated", handler);
    return () => window.removeEventListener("rgm.membership.updated", handler);
  }, [refresh]);

  const purchase = async (plan) => {
    if (!enabled) return;
    if (plan.plan_id === "founding_artist") return;
    setBusyPlan(plan.plan_id);
    try {
      const r = await api.post("/memberships/checkout", {
        plan_id: plan.plan_id,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "subscription_price_not_configured") {
        toast.error("This plan isn't ready to accept payments yet. Admin needs to set the Stripe Price ID.");
      } else if (detail === "already_active_plan") {
        toast.error("You already have this plan active. Use Manage Membership to make changes.");
        refresh();
      } else if (detail === "already_higher_tier") {
        toast.error("You already have RGM Premium (Yearly). No need to downgrade — you're covered.");
        refresh();
      } else if (detail === "already_founding_artist") {
        toast.error("You already have Founding Artist lifetime access — every tier is unlocked for you.");
        refresh();
      } else {
        toast.error(detail || "Checkout failed");
      }
    } finally {
      setBusyPlan(null);
    }
  };

  const openBillingPortal = async () => {
    setBusyPortal(true);
    try {
      const r = await api.post("/memberships/billing-portal");
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Billing portal is unavailable right now.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "no_billable_membership") {
        toast.error("Your membership doesn't have a Stripe billing record. Contact support if you need to make changes.");
      } else if (detail === "stripe_portal_unavailable" || detail === "stripe_session_missing" || detail === "stripe_customer_missing") {
        toast.error("Billing portal is temporarily unavailable — please contact support@rgm.studio for changes.");
      } else if (detail === "stripe_not_configured") {
        toast.error("Billing isn't fully set up. Contact support to make changes.");
      } else {
        toast.error(detail || "Billing portal is unavailable.");
      }
    } finally {
      setBusyPortal(false);
    }
  };

  const activePlanId = me?.plan_id || null;
  const isFounder = activePlanId === "founding_artist";
  const isYearly = activePlanId === "premium_yearly";
  const isMonthly = activePlanId === "premium_monthly";
  const isComplimentary = !!me?.complimentary;
  const isLifetime = !!me?.is_lifetime;
  const canManage = !!me && !isFounder && !isComplimentary;

  const ctaFor = (plan) => {
    const pid = plan.plan_id;
    // Free plan is always Free — nothing to buy.
    if (pid === "free") return { kind: "info", text: me ? (isFounder || isLifetime ? "Included in Lifetime" : "Included with every account") : "Included with every RGM account." };
    if (pid === "founding_artist") return { kind: "invite" };

    // No active membership → normal purchase path.
    if (!me) {
      return { kind: enabled ? "buy" : "soon", text: enabled ? `Choose ${plan.name}` : "Launching soon" };
    }

    // Founding Artist active → every paid card locked with Lifetime.
    if (isFounder || isLifetime) return { kind: "lifetime" };

    // Same plan → Current
    if (pid === activePlanId) return { kind: "current" };

    // Yearly active + monthly card → hide the CTA (never downgrade to monthly).
    if (isYearly && pid === "premium_monthly") return { kind: "covered" };

    // Monthly active + yearly card → Upgrade CTA
    if (isMonthly && pid === "premium_yearly") {
      return { kind: enabled ? "upgrade" : "soon", text: enabled ? "Upgrade to Yearly" : "Launching soon" };
    }

    // Complimentary/other member trying to buy something else → still allowed for now.
    return { kind: enabled ? "buy" : "soon", text: enabled ? `Choose ${plan.name}` : "Launching soon" };
  };

  const skey = statusKey(me);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="membership-page">
      <SharedNavigation />
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        <div className="text-center space-y-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-black">
            RGM Memberships
          </p>
          <h1 className="text-3xl sm:text-5xl font-black">Level up on RGM</h1>
          <p className="text-gray-300 text-sm sm:text-base max-w-2xl mx-auto">
            Pick a plan built for real artists. Every tier unlocks more of what RGM offers.
          </p>
          {!enabled && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-100 text-xs font-black"
              data-testid="membership-toggle-off-banner"
            >
              ⏳ Memberships launching soon. Founding Artist invitations are private.
            </div>
          )}
        </div>

        {/* Active membership recognition strip */}
        {me && (
          <div
            className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-900/25 via-purple-900/10 to-black p-5 sm:p-6"
            data-testid="my-membership-strip"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-black">
                  Your Current RGM Membership
                </p>
                <h2 className="text-2xl sm:text-3xl font-black mt-1" data-testid="my-membership-plan-name">
                  {me.plan_name || me.plan_id}
                  {me.founder_number_label && (
                    <span className="ml-2 text-amber-300 text-lg sm:text-xl">
                      · {me.founder_number_label}
                    </span>
                  )}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                  <span
                    className={`px-2 py-0.5 rounded-full border font-black uppercase tracking-wider ${STATUS_TONE[skey] || STATUS_TONE.active}`}
                    data-testid="my-membership-status-pill"
                  >
                    {STATUS_LABEL[skey] || "Active"}
                  </span>
                  {me.billing_label && (
                    <span className="text-gray-300" data-testid="my-membership-billing-label">
                      {me.billing_label} billing
                    </span>
                  )}
                  {(() => {
                    const rl = formatRenewal(me.renewal_at, isLifetime);
                    return rl ? (
                      <span className="text-gray-400" data-testid="my-membership-renewal">
                        · {rl}
                      </span>
                    ) : null;
                  })()}
                  {isComplimentary && !isFounder && (
                    <span className="text-amber-200" data-testid="my-membership-complimentary">
                      · Complimentary access
                    </span>
                  )}
                </div>
              </div>
              {canManage ? (
                <button
                  onClick={openBillingPortal}
                  disabled={busyPortal}
                  className="px-4 py-2 rounded-lg bg-white text-black font-black text-sm disabled:opacity-60"
                  data-testid="my-membership-manage-btn"
                >
                  {busyPortal ? "Opening portal…" : "Manage Membership"}
                </button>
              ) : (
                <div
                  className="px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-100 text-xs font-black"
                  data-testid="my-membership-lifetime-note"
                >
                  {isFounder ? "🏆 Founding Artist · Lifetime Access" : isComplimentary ? "🎁 Complimentary — no billing" : "No billing required"}
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <p className="text-center text-gray-400" data-testid="membership-loading">Loading plans…</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => {
            const cta = ctaFor(p);
            const isFounderCard = p.plan_id === "founding_artist";
            const isCurrent = cta.kind === "current";
            const isFree = p.plan_id === "free";
            return (
              <div
                key={p.plan_id}
                className={`rounded-2xl border p-5 sm:p-6 space-y-4 ${
                  isCurrent
                    ? "border-emerald-500/50 bg-gradient-to-br from-emerald-900/25 via-purple-900/10 to-black"
                    : isFounderCard
                    ? "border-amber-500/50 bg-gradient-to-br from-amber-900/25 via-purple-900/20 to-black"
                    : "border-white/10 bg-white/[0.03]"
                }`}
                data-testid={`membership-plan-card-${p.plan_id}`}
              >
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-purple-300 font-black">
                    {p.billing === "one_time"
                      ? "One-time"
                      : p.billing === "yearly"
                      ? "Yearly"
                      : p.billing === "monthly"
                      ? "Monthly"
                      : "Free"}
                  </p>
                  <h2 className="text-2xl font-black" data-testid={`membership-plan-name-${p.plan_id}`}>
                    {p.name}
                  </h2>
                  <p className="text-3xl font-black text-white mt-1" data-testid={`membership-plan-price-${p.plan_id}`}>
                    {isFree ? "Free" : `$${(p.price_usd ?? 0).toFixed(2)}`}
                    {p.billing === "monthly" && (
                      <span className="text-sm text-gray-400 font-normal">/mo</span>
                    )}
                    {p.billing === "yearly" && (
                      <span className="text-sm text-gray-400 font-normal">/yr</span>
                    )}
                    {p.billing === "one_time" && (
                      <span className="text-sm text-gray-400 font-normal"> lifetime</span>
                    )}
                  </p>
                </div>
                <ul className="space-y-1.5 text-sm text-gray-200">
                  {(p.benefits || []).map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-amber-300">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA slot */}
                <div className="pt-2">
                  {cta.kind === "current" && (
                    <div className="w-full px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/60 text-emerald-100 font-black text-sm text-center"
                         data-testid={`membership-plan-cta-${p.plan_id}`}>
                      ✓ Current Plan
                    </div>
                  )}
                  {cta.kind === "covered" && (
                    <div className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 font-black text-sm text-center"
                         data-testid={`membership-plan-cta-${p.plan_id}`}>
                      Included in your Yearly plan
                    </div>
                  )}
                  {cta.kind === "lifetime" && (
                    <div className="w-full px-4 py-2 rounded-lg bg-amber-500/15 border border-amber-500/50 text-amber-100 font-black text-sm text-center"
                         data-testid={`membership-plan-cta-${p.plan_id}`}>
                      🏆 Included · Lifetime Access
                    </div>
                  )}
                  {cta.kind === "invite" && (
                    <button
                      disabled
                      className="w-full px-4 py-2 rounded-lg bg-white/5 text-amber-100 font-black text-sm cursor-not-allowed border border-amber-500/40"
                      data-testid={`membership-plan-cta-${p.plan_id}`}
                    >
                      ✉️ Invite Only
                    </button>
                  )}
                  {cta.kind === "info" && (
                    <p className="text-xs text-gray-400" data-testid={`membership-plan-cta-${p.plan_id}`}>
                      {cta.text}
                    </p>
                  )}
                  {cta.kind === "buy" && (
                    <button
                      onClick={() => purchase(p)}
                      disabled={busyPlan === p.plan_id}
                      className="w-full px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-black text-sm"
                      data-testid={`membership-plan-cta-${p.plan_id}`}
                    >
                      {busyPlan === p.plan_id ? "Redirecting…" : cta.text}
                    </button>
                  )}
                  {cta.kind === "upgrade" && (
                    <button
                      onClick={() => purchase(p)}
                      disabled={busyPlan === p.plan_id}
                      className="w-full px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-black text-sm"
                      data-testid={`membership-plan-cta-${p.plan_id}`}
                    >
                      {busyPlan === p.plan_id ? "Redirecting…" : cta.text}
                    </button>
                  )}
                  {cta.kind === "soon" && (
                    <button
                      disabled
                      className="w-full px-4 py-2 rounded-lg bg-white/5 text-gray-400 font-black text-sm cursor-not-allowed"
                      data-testid={`membership-plan-cta-${p.plan_id}`}
                    >
                      {cta.text}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center text-xs text-gray-500">
          Questions?{" "}
          <Link to="/community-guidelines" className="text-purple-300 underline">
            Read the Community Guidelines
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
